import { apiClient } from '@shared/api/client';
import { ApiResponse } from '@shared/api/api.types';
import {
  UserWithRoleDTO,
  GetUsersWithRolesRequestDTO,
  UsersWithRolesResponseDTO,
  UserDTO,
  InviteUserWithRoleDTO,
  InvitationWithRoleResponseDTO,
  UserExistsCheckDTO,
} from '../dtos';

/**
 * User Management API
 * Provides centralized API functions for managing users with roles
 */
export const userManagementApi = {
  /**
   * Get users with their roles using comprehensive filtering and pagination
   * @param params Query parameters for filtering, sorting, and pagination
   * @returns Promise<ApiResponse<UsersWithRolesResponseDTO>>
   */
  async getUsersWithRoles(
    params: GetUsersWithRolesRequestDTO = {}
  ): Promise<ApiResponse<UsersWithRolesResponseDTO>> {
    // Build query string parameters
    const queryParams = new URLSearchParams();

    // Add filtering parameters
    if (params.role !== undefined) {
      queryParams.append('role', params.role);
    }
    if (params.tenantId !== undefined) {
      queryParams.append('tenantId', params.tenantId);
    }
    if (params.domainId !== undefined) {
      queryParams.append('domainId', params.domainId);
    }
    if (params.subjectId !== undefined) {
      queryParams.append('subjectId', params.subjectId);
    }
    if (params.status !== undefined) {
      queryParams.append('status', params.status);
    }
    if (params.email !== undefined) {
      queryParams.append('email', params.email);
    }
    if (params.name !== undefined) {
      queryParams.append('name', params.name);
    }
    if (params.includeExpiredRoles !== undefined) {
      queryParams.append('includeExpiredRoles', params.includeExpiredRoles.toString());
    }
    if (params.includeUsersWithoutRoles !== undefined) {
      queryParams.append('includeUsersWithoutRoles', params.includeUsersWithoutRoles.toString());
    }

    // Add pagination parameters
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }
    if (params.nextToken !== undefined) {
      queryParams.append('nextToken', params.nextToken);
    }

    // Add sorting parameters
    if (params.sortBy !== undefined) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params.sortOrder !== undefined) {
      queryParams.append('sortOrder', params.sortOrder);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/users/with-roles?${queryString}` : '/users/with-roles';

    return apiClient.get<UsersWithRolesResponseDTO>(url);
  },

  /**
   * Get a specific user with role information by user ID
   * @param userId The ID of the user to fetch
   * @param tenantId Optional tenant ID for scoped queries
   * @returns Promise<ApiResponse<UserWithRoleDTO>>
   */
  async getUserWithRoleById(
    userId: string,
    tenantId?: string
  ): Promise<ApiResponse<UserWithRoleDTO>> {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const queryParams = new URLSearchParams();
    if (tenantId) {
      queryParams.append('tenantId', tenantId);
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/${userId}/with-role?${queryString}`
      : `/users/${userId}/with-role`;

    return apiClient.get<UserWithRoleDTO>(url);
  },

  /**
   * Check if a user exists by email
   * @param email Email address to check
   * @returns Promise<ApiResponse<UserExistsCheckDTO>>
   */
  async checkUserExists(email: string): Promise<ApiResponse<UserExistsCheckDTO>> {
    if (!email) {
      throw new Error('Email is required');
    }

    const queryParams = new URLSearchParams();
    queryParams.append('email', encodeURIComponent(email));

    return apiClient.get<UserExistsCheckDTO>(`/users/check-exists?${queryParams.toString()}`);
  },

  /**
   * Invite a user with a specific role
   * @param data Invitation data including email, role, and context
   * @returns Promise<ApiResponse<InvitationWithRoleResponseDTO>>
   */
  async inviteUserWithRole(
    data: InviteUserWithRoleDTO
  ): Promise<ApiResponse<InvitationWithRoleResponseDTO>> {
    if (!data.email || !data.role || !data.tenantId || !data.assignedBy) {
      throw new Error('Email, role, tenantId, and assignedBy are required');
    }

    return apiClient.post<InvitationWithRoleResponseDTO>('/users/invite-with-role', data);
  },

  /**
   * Get all users (without role information) - for basic user queries
   * @param tenantId Optional tenant ID for scoped queries
   * @returns Promise<ApiResponse<UserDTO[]>>
   */
  async getUsers(tenantId?: string): Promise<ApiResponse<UserDTO[]>> {
    const queryParams = new URLSearchParams();
    if (tenantId) {
      queryParams.append('tenantId', tenantId);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/users?${queryString}` : '/users';

    return apiClient.get<UserDTO[]>(url);
  },

  /**
   * Get a specific user by ID (without role information)
   * @param userId The ID of the user to fetch
   * @returns Promise<ApiResponse<UserDTO>>
   */
  async getUserById(userId: string): Promise<ApiResponse<UserDTO>> {
    if (!userId) {
      throw new Error('User ID is required');
    }

    return apiClient.get<UserDTO>(`/users/${userId}`);
  },
};

// Legacy aliases for backward compatibility
export const userWithRoleApi = userManagementApi;

// Export individual API functions for tree-shaking
export const {
  getUsersWithRoles,
  getUserWithRoleById,
  checkUserExists,
  inviteUserWithRole,
  getUsers,
  getUserById,
} = userManagementApi;
